import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { validateAuthToken } from "@/guards/validateAuthToken";
import { validateActiveUser } from "@/guards/validateActiveUser";
import { getProviderRegistrationFlowHint } from "@/lib/providerRegistrationFlowHint";
import {
  getProviderBillingSummary,
  openProviderBillingPortal,
} from "@/app/api/provider/billing";

import { SectionRoute } from "@/components/shared/SectionRoute";
import { PlaceholderSection } from "@/components/placeholder-sections/PlaceholderSection";
import { Button } from "@/components/ui/Button";

export const Route = createFileRoute("/dashboard/billing/")({
  beforeLoad: () => {
    validateAuthToken();
    validateActiveUser();
  },
  component: BillingPage,
});

function BillingPage() {
  const { t } = useTranslation();

  return (
    <div className="pb-8">
      <SectionRoute
        route_one={t("routes.dashboard")}
        route_two={t("routes.billing")}
      />
      <div className="mb-8 max-w-7xl mx-auto">
        {getProviderRegistrationFlowHint() === "SELF_SERVICE" ? (
          <CliniciansPlusBilling />
        ) : (
          <PlaceholderSection type="billing" />
        )}
      </div>
    </div>
  );
}

function CliniciansPlusBilling() {
  const { t } = useTranslation();
  const [history, setHistory] = useState<
    "loading" | "available" | "none" | "error"
  >("loading");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    void getProviderBillingSummary()
      .then((response) => {
        if (cancelled) return;
        if (response.status !== "success") {
          setHistory("error");
          return;
        }
        setHistory(
          response.data.hasBillingHistory ? "available" : "none",
        );
      })
      .catch(() => {
        if (!cancelled) setHistory("error");
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const manageBilling = async () => {
    if (busy) return;
    setBusy(true);
    setError("");

    try {
      await openProviderBillingPortal();
    } catch {
      setError(t("selfServiceBilling.portalError", {
        defaultValue: "Unable to open billing management. Please try again.",
      }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-white rounded-xl border border-neutral-100 p-8 flex flex-col gap-5">
      <h2 className="text-2xl font-semibold">
        {t("selfServiceBilling.title", {
          defaultValue: "Clinicians+ billing",
        })}
      </h2>
      <p>
        {t("selfServiceBilling.description", {
          defaultValue:
            "Manage your subscription, payment methods and invoices securely with Stripe.",
        })}
      </p>

      {history === "loading" && (
        <p role="status">
          {t("selfServiceBilling.loading", {
            defaultValue: "Checking billing history...",
          })}
        </p>
      )}

      {history === "none" && (
        <p>
          {t("selfServiceBilling.noHistory", {
            defaultValue: "No Stripe-managed subscription is linked to this account.",
          })}
        </p>
      )}

      {history === "error" && (
        <p role="alert">
          {t("selfServiceBilling.historyError", {
            defaultValue:
              "Unable to load billing history. Refresh this page to retry.",
          })}
        </p>
      )}

      {history === "available" && (
        <div>
          <Button
            type="button"
            disabled={busy}
            isLoading={busy}
            onClick={() => void manageBilling()}
          >
            {t("selfServiceBilling.manage", {
              defaultValue: "Manage subscription and invoices",
            })}
          </Button>
        </div>
      )}

      {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
    </section>
  );
}
